import { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import Select from 'react-select';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { Fade, Tooltip } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const RoleDetail = () => {
    // Session validation
    useSessionValidation();
    
    const { id } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }
    }, [isAuthenticated, companyId]);

    const [selectedOption, setSelectedOption] = useState({ value: '1', label: 'Master' });
    const [formData, setFormData] = useState({
        Name: '',
        Claims: [],
        UpdatedBy: userId,
    });

    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        fetchData('RoleApi', `/RoleDetails/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111' && response.response_data) {
                    const { RoleVM } = response.response_data;
                    setFormData((prevFormData) => ({
                        ...prevFormData,
                        ...RoleVM,
                        Claims: response.response_data.Claims ? response.response_data.Claims.map((claim) => ({ ...claim })) : [],
                    }));
                }
            })
            .catch((error) => {
                console.error('Error fetching Role Details:', error);
                setErrorMessage('Error fetching Role Details. Please try again.');
            });
    }, [id]);

    const getClaimRange = () => {
        const startIndex = (parseInt(selectedOption.value, 10) - 1) * 5;
        const endIndex = startIndex + 4;
        return { startIndex, endIndex };
    };

    const handleDropdownChange = (newSelectedOption) => {
        setSelectedOption(newSelectedOption);
    };

    const handleChange = (index) => {
        const updatedClaims = [...formData.Claims];
        updatedClaims[index].IsSelected = !updatedClaims[index].IsSelected;
        setFormData((prevFormData) => ({ ...prevFormData, Claims: updatedClaims }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        updateData('RoleApi', `${id}`, formData, token)
            .then((response) => {
                setSuccessMessage('Record updated successfully.');
                setErrorMessage('');
                history.push(`/roleDetail/${id}`);
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setErrorMessage('Error updating record. Please try again.');
            });
    };

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '0',
                position: 'relative',
                marginTop: '-20px'
            }}>
                <style>{`
                    @keyframes slideIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .input-field-detail {
                        transition: all 0.3s ease;
                        border: 2px solid transparent;
                    }
                    .input-field-detail:hover {
                        border-color: #e0e0e0;
                    }
                    .action-button-detail {
                        transition: all 0.3s ease;
                        text-transform: none;
                        font-weight: 600;
                        letter-spacing: 0.5px;
                    }
                    .action-button-detail:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
                    }
                    .section-card-detail {
                        animation: slideIn 0.6s ease-out forwards;
                        opacity: 0;
                    }
                    .section-card-detail:nth-child(1) { animation-delay: 0.1s; }
                    .section-card-detail:nth-child(2) { animation-delay: 0.2s; }
                    .section-card-detail:nth-child(3) { animation-delay: 0.3s; }
                    .claim-checkbox {
                        transition: all 0.2s ease;
                    }
                    .claim-checkbox:hover {
                        transform: translateX(4px);
                        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15) !important;
                    }
                    .claim-checkbox:active {
                        transform: scale(0.98);
                    }
                    /* Custom scrollbar */
                    ::-webkit-scrollbar {
                        width: 8px;
                        height: 8px;
                    }
                    ::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 10px;
                    }
                    ::-webkit-scrollbar-thumb {
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        border-radius: 10px;
                    }
                    ::-webkit-scrollbar-thumb:hover {
                        background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
                    }
                `}</style>

                <div className="suite-page-panel" style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '8px 12px',
                    position: 'relative'
                }}>
                    {/* Header Section */}
                    <div className="suite-section-heading" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '6px',
                        paddingBottom: '4px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                                background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                                padding: '6px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: "0 2px 6px rgba(13, 118, 110, 0.3)"
                            }}>
                                <SecurityIcon style={{ fontSize: '18px', color: 'white' }} />
                            </div>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <h1 style={{
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        margin: 0,
                                        letterSpacing: '-0.3px'
                                    }}>
                                        Role Details
                                    </h1>
                                    <Tooltip title="Manage role permissions and claims" arrow placement="right">
                                        <InfoIcon style={{ fontSize: '14px', color: "#0d766e", cursor: 'pointer' }} />
                                    </Tooltip>
                                </div>
                                <p style={{ margin: 0, color: '#666', fontSize: '10px', marginTop: '2px' }}>
                                    Configure role permissions and claims
                                </p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <Tooltip title="Save changes" arrow>
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    startIcon={<SaveIcon />}
                                    className="action-button-detail"
                                    style={{
                                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        boxShadow: '0 2px 6px rgba(17, 153, 142, 0.3)',
                                        minHeight: '28px'
                                    }}
                                >
                                    Update
                                </Button>
                            </Tooltip>
                            <Tooltip title="Return to role list" arrow>
                                <Button
                                    variant="outlined"
                                    component={Link}
                                    to="/RoleIndex"
                                    startIcon={<ArrowBackIcon />}
                                    className="action-button-detail"
                                    style={{
                                        borderColor: "#0d766e",
                                        color: "#0d766e",
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        borderWidth: '1.5px',
                                        minHeight: '28px'
                                    }}
                                >
                                    Back to List
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    {/* Error/Success Messages */}
                    {errorMessage && (
                        <div style={{
                            padding: '10px',
                            marginBottom: '10px',
                            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                            color: 'white',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <InfoIcon style={{ fontSize: '18px' }} />
                            {errorMessage}
                        </div>
                    )}
                    {successMessage && (
                        <div style={{
                            padding: '10px',
                            marginBottom: '10px',
                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                            color: 'white',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <CheckCircleIcon style={{ fontSize: '18px' }} />
                            {successMessage}
                        </div>
                    )}

                    {/* Role Information Section */}
                    <div className="section-card-detail" style={{
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                        padding: '10px',
                        borderRadius: '10px',
                        marginBottom: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            background: "rgba(13, 118, 110, 0.9)",
                            borderRadius: '50%',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <AdminPanelSettingsIcon style={{ fontSize: '16px', color: 'white' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <SecurityIcon style={{ color: "#0d766e", fontSize: '18px' }} />
                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                                Role Information
                            </h3>
                        </div>
                        <div>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#4a5568',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px'
                            }}>
                                <AdminPanelSettingsIcon style={{ fontSize: '13px' }} />
                                Role Name
                            </label>
                            <input
                                className="input-field-detail"
                                value={formData.Name}
                                name='Name'
                                type="text"
                                placeholder="Role Name"
                                readOnly
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    fontSize: '13px',
                                    borderRadius: '8px',
                                    border: "1.5px solid #0d766e",
                                    background: '#fafafa',
                                    outline: 'none',
                                    fontWeight: '500',
                                    cursor: 'not-allowed'
                                }}
                            />
                        </div>
                    </div>

                    {/* Claims Management Section */}
                    <div className="section-card-detail" style={{
                        background: 'white',
                        padding: '10px',
                        borderRadius: '10px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                        border: '1px solid #e8eaf0',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Decorative gradient bar */}
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            background: "linear-gradient(90deg, #0d766e 0%, #164e63 100%)"
                        }} />

                        {/* Header with icon */}
                        <div className="suite-section-heading" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '10px',
                            paddingBottom: '8px',
                            borderBottom: '1px solid #f0f2f5'
                        }}>
                            <div style={{
                                background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                                padding: '6px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: "0 2px 6px rgba(13, 118, 110, 0.3)"
                            }}>
                                <SecurityIcon style={{ fontSize: '16px', color: 'white' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    color: '#2d3748',
                                    letterSpacing: '-0.2px'
                                }}>
                                    Role Claims
                                </h3>
                                <p style={{
                                    margin: 0,
                                    fontSize: '10px',
                                    color: '#718096',
                                    marginTop: '1px'
                                }}>
                                    Manage permissions for this role
                                </p>
                            </div>
                        </div>

                        {/* Claim Category Selector */}
                        <div style={{
                            marginBottom: '10px',
                            padding: '8px',
                            background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '10px',
                                fontWeight: '700',
                                color: '#4a5568',
                                marginBottom: '6px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px'
                            }}>
                                <InfoIcon style={{ fontSize: '12px', color: "#0d766e" }} />
                                Select Claim Category
                            </label>
                            <Select
                                options={[
                                    { value: '1', label: 'Master' },
                                    { value: '2', label: 'Transaction' },
                                    { value: '3', label: 'Accounts' },
                                    { value: '4', label: 'Reports' },
                                    { value: '5', label: 'Administration' },
                                ]}
                                value={selectedOption}
                                onChange={handleDropdownChange}
                                styles={{
                                    control: (base, state) => ({
                                        ...base,
                                        borderColor: state.isFocused ? '#667eea' : '#cbd5e0',
                                        borderWidth: '1.5px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        minHeight: '32px',
                                        boxShadow: state.isFocused ? '0 0 0 2px rgba(102, 126, 234, 0.1)' : 'none',
                                        '&:hover': {
                                            borderColor: '#667eea'
                                        },
                                        background: 'white'
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? '#edf2f7' : 'white',
                                        color: state.isSelected ? 'white' : '#2d3748',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        padding: '6px 10px'
                                    })
                                }}
                            />
                        </div>

                        {/* Claims Statistics Cards */}
                        <div className="suite-responsive-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '8px',
                            marginBottom: '10px'
                        }}>
                            <div style={{
                                background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                                padding: '10px 12px',
                                borderRadius: '8px',
                                color: 'white',
                                boxShadow: "0 2px 8px rgba(13, 118, 110, 0.3)",
                                position: 'relative',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: '-8px',
                                    right: '-8px',
                                    width: '40px',
                                    height: '40px',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '50%'
                                }} />
                                <div style={{
                                    fontSize: '22px',
                                    fontWeight: '700',
                                    lineHeight: '1',
                                    position: 'relative',
                                    zIndex: 1
                                }}>
                                    {formData.Claims ? formData.Claims.filter((c, index) => {
                                        const { startIndex, endIndex } = getClaimRange();
                                        return index >= startIndex && index <= endIndex;
                                    }).length : 0}
                                </div>
                                <div style={{
                                    fontSize: '10px',
                                    opacity: 0.95,
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px',
                                    flex: 1,
                                    position: 'relative',
                                    zIndex: 1
                                }}>
                                    Category Claims
                                </div>
                                <SecurityIcon style={{ fontSize: '20px', opacity: 0.9, position: 'relative', zIndex: 1 }} />
                            </div>
                            <div style={{
                                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                color: 'white',
                                boxShadow: '0 2px 8px rgba(17, 153, 142, 0.3)',
                                position: 'relative',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: '-8px',
                                    right: '-8px',
                                    width: '40px',
                                    height: '40px',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '50%'
                                }} />
                                <div style={{
                                    fontSize: '22px',
                                    fontWeight: '700',
                                    lineHeight: '1',
                                    position: 'relative',
                                    zIndex: 1
                                }}>
                                    {formData.Claims ? formData.Claims.filter((c, index) => {
                                        const { startIndex, endIndex } = getClaimRange();
                                        return index >= startIndex && index <= endIndex && c.IsSelected;
                                    }).length : 0}
                                </div>
                                <div style={{
                                    fontSize: '10px',
                                    opacity: 0.95,
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px',
                                    flex: 1,
                                    position: 'relative',
                                    zIndex: 1
                                }}>
                                    Active Claims
                                </div>
                                <CheckCircleIcon style={{ fontSize: '20px', opacity: 0.9, position: 'relative', zIndex: 1 }} />
                            </div>
                        </div>

                        {/* Claims List Container */}
                        <div style={{
                            background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
                            padding: '8px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div className="suite-section-heading" style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '8px',
                                paddingBottom: '6px',
                                borderBottom: '1px solid #cbd5e0'
                            }}>
                                <div style={{
                                    fontSize: '10px',
                                    fontWeight: '700',
                                    color: '#2d3748',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    Available Claims:
                                </div>
                                <div style={{
                                    fontSize: '9px',
                                    color: '#718096',
                                    fontWeight: '600',
                                    background: 'white',
                                    padding: '3px 8px',
                                    borderRadius: '10px',
                                    border: '1px solid #cbd5e0'
                                }}>
                                    {selectedOption.label} Category
                                </div>
                            </div>

                            {/* Scrollable Claims List */}
                            <div style={{
                                maxHeight: '280px',
                                overflowY: 'auto',
                                padding: '2px'
                            }}>
                                {formData.Claims && formData.Claims.length > 0 ? (
                                    formData.Claims.map((claim, index) => {
                                        const { startIndex, endIndex } = getClaimRange();
                                        const shouldIncludeClaim = index >= startIndex && index <= endIndex;

                                        if (shouldIncludeClaim) {
                                            return (
                                                <div
                                                    key={index}
                                                    className="claim-checkbox"
                                                    onClick={() => handleChange(index)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        padding: '8px 10px',
                                                        marginBottom: '4px',
                                                        background: claim.IsSelected
                                                            ? 'linear-gradient(135deg, rgba(17, 153, 142, 0.15) 0%, rgba(56, 239, 125, 0.15) 100%)'
                                                            : 'white',
                                                        borderRadius: '6px',
                                                        border: claim.IsSelected ? '1.5px solid #11998e' : '1.5px solid #e2e8f0',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        boxShadow: claim.IsSelected ? '0 1px 4px rgba(17, 153, 142, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.05)'
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name={`Claims[${index}].IsSelected`}
                                                        id={`claimCheckbox${index}`}
                                                        checked={claim.IsSelected}
                                                        onChange={() => handleChange(index)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            width: '15px',
                                                            height: '15px',
                                                            cursor: 'pointer',
                                                            accentColor: '#11998e'
                                                        }}
                                                    />
                                                    <label
                                                        htmlFor={`claimCheckbox${index}`}
                                                        style={{
                                                            fontSize: '12px',
                                                            fontWeight: claim.IsSelected ? '600' : '500',
                                                            color: claim.IsSelected ? '#11998e' : '#2d3748',
                                                            flex: 1,
                                                            cursor: 'pointer',
                                                            margin: 0,
                                                            letterSpacing: '0.1px'
                                                        }}
                                                    >
                                                        {claim.ClaimValue}
                                                    </label>
                                                    {claim.IsSelected && (
                                                        <div style={{
                                                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                                            borderRadius: '50%',
                                                            padding: '3px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)'
                                                        }}>
                                                            <CheckCircleIcon style={{ fontSize: '13px', color: 'white' }} />
                                                        </div>
                                                    )}
                                                    <input type="hidden" name={`Claims[${index}].ClaimType`} value={claim.ClaimType} />
                                                    <input type="hidden" name={`Claims[${index}].ClaimValue`} value={claim.ClaimValue} />
                                                    <input type="hidden" name={`Claims[${index}].LogInClaims`} value={claim.LogInClaims} />
                                                </div>
                                            );
                                        }
                                        return null;
                                    })
                                ) : (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '30px 15px',
                                        background: 'white',
                                        borderRadius: '6px',
                                        border: '2px dashed #cbd5e0'
                                    }}>
                                        <SecurityIcon style={{ fontSize: '36px', color: '#cbd5e0', marginBottom: '8px' }} />
                                        <p style={{
                                            fontSize: '11px',
                                            color: '#718096',
                                            margin: 0,
                                            fontWeight: '500'
                                        }}>
                                            No claims available for this category.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    fontFamily: 'serif',
                    fontWeight: '600',
                    color: '#2c3e50',
                    fontSize: '13px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    zIndex: 1000
                }}>
                    <CopyrightIcon style={{ fontSize: '18px', color: "#0d766e" }} />
                    <span>EFFTABS SOLUTIONS LLP</span>
                </div>
            </div>
        </Fade>
    );
};

export default RoleDetail;
